import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Mail, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';

export const ServiceInfrastructure = () => {
  return (
    <div className="min-h-screen pt-24 pb-16 relative overflow-hidden">
      {/* Décors d'ambiance subtils */}
      <div className="absolute top-40 -left-20 w-72 h-72 bg-gradient-to-br from-blue-200/20 to-cyan-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-[700px] -right-20 w-80 h-80 bg-gradient-to-br from-orange-200/20 to-pink-200/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-40 left-1/3 w-96 h-96 bg-gradient-to-br from-purple-200/15 to-blue-200/15 rounded-full blur-3xl pointer-events-none" />

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-8">
          <Link to="/services" className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center mb-4">
            <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
            Retour aux services
          </Link>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Projets d'infrastructure
          </h1>
          <p className="text-xl text-gray-600">
            J'accompagne vos projets d'infrastructure : renouvellement de serveurs, mise en place ou refonte de réseau, déploiement Wi-Fi, segmentation, sécurisation des accès.
          </p>
        </div>

        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <p className="text-lg text-gray-800">
              Nous définissons ensemble une architecture adaptée à la taille de votre entreprise, à vos contraintes métiers et à votre budget, avec un déploiement planifié pour limiter les interruptions.
            </p>
          </CardContent>
        </Card>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Types de projets que j'accompagne</h2>
          <div className="space-y-3">
            {[
              'Renouvellement ou migration de serveurs (physiques ou virtuels)',
              'Conception et mise en place d\'architectures réseau',
              'Déploiement de solutions Wi-Fi professionnelles',
              'Segmentation réseau pour séparer les usages (production, bureautique, invités)',
              'Mise en place de solutions de virtualisation',
              'Sécurisation des accès (VPN, pare-feu, authentification)',
              'Projets de migration vers le cloud ou hybride',
              'Optimisation et modernisation d\'infrastructures existantes'
            ].map((item, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Ce que cela vous apporte</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Infrastructure fiable</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Des systèmes modernes et stables pour supporter votre activité</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Performances améliorées</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Réseau rapide, accès fluides, moins de lenteurs</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Évolutivité</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Une architecture qui peut grandir avec votre entreprise</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Déploiement maîtrisé</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">Planning clair, étapes validées, interruptions minimisées</p>
              </CardContent>
            </Card>
          </div>
        </div>

        <Card className="mb-8 border-gray-300">
          <CardContent className="pt-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Approche sur mesure</h3>
            <p className="text-gray-600 mb-4">
              Chaque projet d'infrastructure est unique. Je commence toujours par un audit de l'existant et une analyse de vos besoins, 
              puis je vous propose une solution adaptée avec un chiffrage transparent.
            </p>
            <p className="text-gray-600">
              Le déploiement est planifié avec vous pour minimiser l'impact sur votre activité, avec des points de contrôle réguliers.
            </p>
          </CardContent>
        </Card>

        <div className="bg-gradient-to-br from-blue-50 to-gray-50 rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Un projet d'infrastructure en vue ?</h3>
          <p className="text-gray-600 mb-6">
            Discutons de vos objectifs et voyons comment structurer votre projet.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
              <a href="https://outlook.office.com/book/SAADINFORMATIQUE1@saad-informatique.fr/?ismsaljsauthenabled" target="_blank" rel="noopener noreferrer">
                <Phone className="w-5 h-5 mr-2" />
                Prendre RDV
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50">
              <Link to="/contact">
                <Mail className="w-5 h-5 mr-2" />
                Me contacter
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};